// =============================================================================
// ReasonCombobox - Selettore causale di assenza con ricerca testuale
// =============================================================================
// Dropdown ricercabile (digita per filtrare, frecce + Invio, click) che mostra
// nome, colore e icona di ogni causale. Speculare a ResourceCombobox.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { resolveIcon } from '../scheduler/utils/exceptionStyle';

export interface ReasonOption {
  id: string;
  name: string;
  color: string;
  icon: string;
}

interface ReasonComboboxProps {
  reasons: ReasonOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Testo mostrato quando nessuna causale corrisponde alla ricerca. */
  noMatchLabel?: string;
  className?: string;
}

export function ReasonCombobox({
  reasons,
  value,
  onChange,
  placeholder,
  noMatchLabel = '—',
  className,
}: ReasonComboboxProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => reasons.find((r) => r.id === value), [reasons, value]);

  const [query, setQuery] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Mantieni l'input allineato alla selezione quando cambia dall'esterno.
  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected]);

  // Quando l'input mostra esattamente il nome selezionato non stiamo filtrando.
  const isFiltering = query.trim() !== '' && query !== selected?.name;
  const filtered = useMemo(() => {
    if (!isFiltering) return reasons;
    const q = query.trim().toLowerCase();
    return reasons.filter((r) => r.name.toLowerCase().includes(q));
  }, [reasons, query, isFiltering]);

  // Reset evidenziazione all'apertura o al cambio dei risultati.
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  // Chiudi cliccando fuori.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndSync();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  const closeAndSync = () => {
    setOpen(false);
    setQuery(selected?.name ?? '');
  };

  const selectOption = (r: ReasonOption) => {
    onChange(r.id);
    setQuery(r.name);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered.length > 0) {
        e.preventDefault();
        selectOption(filtered[Math.min(highlight, filtered.length - 1)]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAndSync();
    }
  };

  const SelectedIcon = selected ? resolveIcon(selected.icon) : null;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        {SelectedIcon && !isFiltering ? (
          <SelectedIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: selected!.color }}
          />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        )}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-9 py-2 input"
        />
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </div>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">{noMatchLabel}</li>
          ) : (
            filtered.map((r, idx) => {
              const Icon = resolveIcon(r.icon);
              return (
                <li
                  key={r.id}
                  role="option"
                  aria-selected={r.id === value}
                  onMouseEnter={() => setHighlight(idx)}
                  // mousedown invece di click: previene il blur dell'input prima della selezione
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(r);
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer',
                    idx === highlight ? 'bg-emerald-50' : 'hover:bg-gray-50',
                  )}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: r.color }} />
                  <span className="flex-1 min-w-0 font-medium text-gray-900 truncate">{r.name}</span>
                  {r.id === value && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
